import { useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from 'react';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconEdit, IconTrash } from '../../components/icons';
import { Modal } from '../../components/Modal';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';

interface ChartOfAccountRow {
  id: number;
  code: string;
  name: string;
  account_type: string;
  parent_id: number | null;
}

interface AccountForm {
  code: string;
  name: string;
  account_type: string;
  parent_id: number | '';
}

const ACCOUNT_TYPES = [
  { value: 'asset', label: 'Asset' },
  { value: 'liability', label: 'Liability' },
  { value: 'equity', label: 'Equity' },
  { value: 'revenue', label: 'Revenue' },
  { value: 'expense', label: 'Expense' },
];

const typeLabel = (value: string) => ACCOUNT_TYPES.find((t) => t.value === value)?.label ?? value;

const emptyForm: AccountForm = { code: '', name: '', account_type: 'asset', parent_id: '' };

// Depth is computed client-side by walking parent_id up to the root — the
// backend stores a flat self-referencing table, not a materialized tree.
function depthOf(id: number | null, byId: Map<number, ChartOfAccountRow>): number {
  let depth = 0;
  let current = id !== null ? byId.get(id) : undefined;
  while (current) {
    depth++;
    current = current.parent_id !== null ? byId.get(current.parent_id) : undefined;
  }
  return depth;
}

export function ChartOfAccounts() {
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [items, setItems] = useState<ChartOfAccountRow[] | null>(null);
  const [editing, setEditing] = useState<'new' | ChartOfAccountRow | null>(null);
  const [form, setForm] = useState<AccountForm>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const load = () => api.get<ChartOfAccountRow[]>('/chart-of-accounts').then((res) => setItems(res.data));

  useEffect(() => {
    load();
  }, []);

  const byId = useMemo(() => new Map((items ?? []).map((item) => [item.id, item])), [items]);

  const openNew = () => {
    setForm(emptyForm);
    setErrors({});
    setEditing('new');
  };

  const openEdit = (item: ChartOfAccountRow) => {
    setForm({ code: item.code, name: item.name, account_type: item.account_type, parent_id: item.parent_id ?? '' });
    setErrors({});
    setEditing(item);
  };

  const set = (key: keyof AccountForm) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const payload = { ...form, parent_id: form.parent_id === '' ? null : form.parent_id };
    try {
      if (editing === 'new') await api.post('/chart-of-accounts', payload);
      else if (editing) await api.put(`/chart-of-accounts/${editing.id}`, payload);
      setEditing(null);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async (item: ChartOfAccountRow) => {
    if (!(await confirmDelete(`Delete "${item.code} — ${item.name}"?`))) return;
    try {
      await api.delete(`/chart-of-accounts/${item.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const parentOptions = (items ?? []).filter((item) => editing === 'new' || item.id !== (editing as ChartOfAccountRow)?.id);

  return (
    <>
      <header className="page-header">
        <h1>Chart of Accounts</h1>
        <button type="button" className="btn btn-primary" onClick={openNew}>
          + Add
        </button>
      </header>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Type</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items?.map((item) => (
                <tr key={item.id}>
                  <td className="muted">{item.code}</td>
                  <td style={{ paddingLeft: `${depthOf(item.parent_id, byId) * 1.5 + 1}rem` }}>{item.name}</td>
                  <td>
                    <span className="badge badge-neutral">{typeLabel(item.account_type)}</span>
                  </td>
                  <td className="right">
                    <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(item)} />
                    <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(item)} />
                  </td>
                </tr>
              ))}
              {items?.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted center">
                    No accounts yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <Modal title={editing === 'new' ? 'Add account' : 'Edit account'} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label>
              Code *
              <input value={form.code} onChange={set('code')} required maxLength={20} />
              {errors.code && <small className="error">{errors.code[0]}</small>}
            </label>
            <label>
              Account Type *
              <select value={form.account_type} onChange={set('account_type')}>
                {ACCOUNT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              {errors.account_type && <small className="error">{errors.account_type[0]}</small>}
            </label>
            <label className="span-2">
              Name *
              <input value={form.name} onChange={set('name')} required />
              {errors.name && <small className="error">{errors.name[0]}</small>}
            </label>
            <label className="span-2">
              Parent Account
              <select value={form.parent_id} onChange={set('parent_id')}>
                <option value="">None (top-level)</option>
                {parentOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.code} — {option.name}
                  </option>
                ))}
              </select>
              {errors.parent_id && <small className="error">{errors.parent_id[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
